'use client';

import { useEffect } from 'react';
import { useIdPath } from '@/contexts/IdPathProvider';
import { baseGet } from '@/utils/api/base';
import apiEndPointList from '@/utils/constant/apiEndPointsList';

interface PlausibleScriptResponse {
    success: boolean;
    data?: {
        scriptUrl: string;
        domain: string;
    };
    message?: string;
}

async function getPlausibleScript(idPath: string): Promise<PlausibleScriptResponse | null> {
    try {
        const response = await baseGet<PlausibleScriptResponse>({
            endpoint: apiEndPointList.analytics.GET_PLAUSIBLE_SCRIPT(idPath),
        });
        return response.data;
    } catch (error) {
        console.error('[Plausible] Error fetching script:', error);
        return null;
    }
}

export default function PlausibleAnalytics() {
    const { idPath } = useIdPath();

    useEffect(() => {
        if (!idPath) return;

        getPlausibleScript(idPath).then((result) => {
            if (!result?.success || !result.data?.scriptUrl) {
                return;
            }

            const { scriptUrl, domain } = result.data;

            const script = document.createElement('script');
            script.defer = true;
            script.src = scriptUrl;
            document.head.appendChild(script);

            script.onload = () => {
                console.log('[Plausible] Script loaded successfully');
                if (window.plausible) {
                    window.plausible.init({ domain });
                }
            };

            script.onerror = () => {
                console.error('[Plausible] Script failed to load');
            };
        });
    }, [idPath]);

    return null;
}

declare global {
    interface Window {
        plausible?: {
            (event: string, options?: { props?: Record<string, string> }): void;
            q: unknown[];
            init?: (options?: { domain?: string }) => void;
        };
    }
}
